import React, { useEffect, useMemo } from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { connect } from 'react-redux'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import { getBundledFlightPricePerNight, getBundledWithFlightOfferDisplayPricing } from 'lib/offer/getOfferDisplayPricing'
import config from 'constants/config'
import * as Analytics from 'analytics/analytics'
import { getCheapestPackageForDuration } from '../Common/getCheapestPackageForDuration'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowFlightPackage from 'components/Luxkit/PricePoints/PriceRowFlightPackage'
import LuxPlusPriceStack from 'loyalty/components/LuxPlusPriceStack'
import { OptimizelyExperiments, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'

interface Props {
  duration?: number;
  offer: App.Offer | App.OfferSummary;
  airportCode: string;
  isPerNightPricingTestEnabled: boolean;
}

function BundledHotelPriceContent({
  duration,
  airportCode,
  offer,
  isPerNightPricingTestEnabled,
}: Props) {
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER
  const lowestPricePackage = useMemo<App.Package | null>(() => getCheapestPackageForDuration({
    enableDurationSearch,
    offer,
    duration,
  }), [offer, duration, enableDurationSearch])

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      Analytics.trackClientEvent({
        action: `duration_not_finite::${duration}`,
        subject: 'BundledHotelPriceContent',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [duration])

  if (!lowestPricePackage) {
    return null
  }

  const { price, memberPrice, value, duration: durationToDisplay } = lowestPricePackage
  const flightPrice = offer.flightPrices[airportCode]
  const result = getBundledWithFlightOfferDisplayPricing(!!offer.bundledWithFlightsOnly, flightPrice, price, memberPrice, value)

  if (result.isBundledFlightPriceDisplayable) {
    const { packagedTotal, packagedMemberTotal, totalValue, flightPrice, shouldShowValue } = isPerNightPricingTestEnabled ? getBundledFlightPricePerNight(result, durationToDisplay) : result
    const saleUnit = isPerNightPricingTestEnabled ? 'night*' : 'total*'
    return (
      <div>
        {offer.badge && <OfferBadgeTag badge={offer.badge} />}
        <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
          {pluralizeToString('night', durationToDisplay)}{flightPrice && ' + flights from'}
        </PriceRowPriceCaption>
        <LuxPlusPriceStack
          size="S"
          price={Math.ceil(packagedTotal!)}
          saleUnit={saleUnit}
          memberPrice={Math.ceil(packagedMemberTotal!)}
          loyaltyConfig={offer.luxPlus}
        />
        {shouldShowValue && <PriceRowValueDiscountWithCaption
          size="M"
          value={Math.ceil(totalValue!)}
        />}
      </div>
    )
  }

  return <PriceRowFlightPackage price={undefined} product="hotel" />
}

function mapStateToProps(state: App.State) {
  return {
    airportCode: getDefaultAirportCode(state),
    isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(state, OptimizelyExperiments.pricePerNight),
  }
}

export default connect(mapStateToProps)(BundledHotelPriceContent)
